import { useEffect, useState } from "react";
import {
  ApiError,
  api,
  type CasoDetalle as CasoT,
  type Categoria,
  type TipoFalla,
  type TipoSolicitud,
} from "@/lib/api";
import { useToast } from "@/ui/Toast";

const LIBRE = "__libre__";

// Categorías que llevan un "tipo" en el campo tipo_detalle, cada una con su
// catálogo (Configuración → Tipos de falla / Tipos de solicitud). Falla exige
// su tipo (alimenta "fallas recurrentes por modelo"); solicitud es opcional.
function catTipada(c?: string | null): c is "falla" | "solicitud" {
  return c === "falla" || c === "solicitud";
}

/**
 * Clasificación del caso (categoría + tipo de falla/solicitud) en el detalle.
 *
 * Antes solo se capturaba al CREAR el caso y quedaba invisible/no editable.
 * Aquí se muestra siempre y, en casos abiertos (admin/soporte), se edita inline
 * vía PATCH /api/casos/{id} (que ya acepta tipo_categoria/tipo_detalle).
 *
 * `tipo_detalle` lo comparten falla y solicitud; al guardar solo lo tocamos
 * cuando es relevante: si la categoría es falla/solicitud se manda el tipo
 * elegido; si se SALE de una categoría tipada se limpia; en cualquier otro caso
 * no se manda.
 */
export default function CasoClasificacionPanel({
  caso,
  editable,
  onUpdated,
}: {
  caso: CasoT;
  editable: boolean;
  onUpdated: (c: CasoT) => void;
}) {
  const toast = useToast();
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [tiposFalla, setTiposFalla] = useState<TipoFalla[]>([]);
  const [tiposSolicitud, setTiposSolicitud] = useState<TipoSolicitud[]>([]);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Estado de edición (se siembra al abrir).
  const [cat, setCat] = useState("");
  const [tipoSel, setTipoSel] = useState(""); // "" | id | LIBRE
  const [tipoDetalle, setTipoDetalle] = useState("");

  useEffect(() => {
    api.get<Categoria[]>("/api/categorias-caso").then(setCategorias).catch(() => {});
    api.get<TipoFalla[]>("/api/tipos-falla").then(setTiposFalla).catch(() => {});
    api
      .get<TipoSolicitud[]>("/api/tipos-solicitud")
      .then(setTiposSolicitud)
      .catch(() => {});
  }, []);

  // Etiqueta visible de la categoría guardada (slug → nombre del catálogo).
  const nombreCat =
    categorias.find((c) => c.slug === caso.tipo_categoria)?.nombre ||
    caso.tipo_categoria ||
    null;

  // Catálogo y textos según la categoría EN EDICIÓN.
  const esFallaEdit = cat === "falla";
  const tiposActivos = esFallaEdit
    ? tiposFalla
    : cat === "solicitud"
      ? tiposSolicitud
      : [];
  const tipoLabel = esFallaEdit ? "Tipo de falla" : "Tipo de solicitud";

  function abrirEdicion() {
    setCat(caso.tipo_categoria ?? "");
    setTipoDetalle(caso.tipo_detalle ?? "");
    // Si el tipo guardado coincide con uno del catálogo, lo preseleccionamos;
    // si no (texto libre), abrimos el campo manual.
    if (catTipada(caso.tipo_categoria) && caso.tipo_detalle) {
      const pool = caso.tipo_categoria === "falla" ? tiposFalla : tiposSolicitud;
      const t = pool.find((x) => x.nombre === caso.tipo_detalle);
      setTipoSel(t ? String(t.id) : LIBRE);
    } else {
      setTipoSel("");
    }
    setEditando(true);
  }

  async function guardar() {
    if (cat === "falla" && tipoDetalle.trim() === "") {
      toast.error(
        "Falta el tipo de falla",
        "Elige el tipo de falla (o 'escribir manualmente') para guardar.",
      );
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body: any = { tipo_categoria: cat || null };
    if (catTipada(cat)) body.tipo_detalle = tipoDetalle.trim() || null;
    else if (catTipada(caso.tipo_categoria)) body.tipo_detalle = null;

    setGuardando(true);
    try {
      const updated = await api.patch<CasoT>(`/api/casos/${caso.id}`, body);
      onUpdated(updated);
      toast.success("Clasificación actualizada");
      setEditando(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="border-t border-ink-700 pt-3">
      <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
        Clasificación
      </p>

      {!editando ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="text-zinc-300">
            <span className="text-zinc-500">Categoría:</span>{" "}
            {nombreCat ?? "— sin clasificar —"}
          </span>
          {catTipada(caso.tipo_categoria) && (
            <span className="text-zinc-300">
              <span className="text-zinc-500">
                {caso.tipo_categoria === "falla"
                  ? "Tipo de falla:"
                  : "Tipo de solicitud:"}
              </span>{" "}
              {caso.tipo_detalle ||
                (caso.tipo_categoria === "falla" ? (
                  <span className="text-rose-400">sin capturar</span>
                ) : (
                  <span className="text-zinc-500">—</span>
                ))}
            </span>
          )}
          {editable && (
            <button
              onClick={abrirEdicion}
              className="text-xs text-accent-400 hover:underline"
            >
              Editar
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="w-full sm:w-auto">
            <label className="text-[11px] text-zinc-500">Categoría</label>
            <select
              className="input mt-0.5 w-full sm:w-auto"
              value={cat}
              onChange={(e) => {
                setCat(e.target.value);
                // Cambiar de categoría descarta el tipo en edición.
                setTipoSel("");
                setTipoDetalle("");
              }}
            >
              <option value="">— sin clasificar —</option>
              {categorias
                .filter((c) => c.activo || c.slug === caso.tipo_categoria)
                .map((c) => (
                  <option key={c.id} value={c.slug}>
                    {c.nombre}
                  </option>
                ))}
            </select>
          </div>

          {catTipada(cat) && (
            <div className="w-full sm:w-auto">
              <label className="text-[11px] text-zinc-500">
                {tipoLabel}{" "}
                {esFallaEdit && <span className="text-rose-400">*</span>}
              </label>
              <select
                className="input mt-0.5 w-full sm:w-auto"
                value={tipoSel}
                onChange={(e) => {
                  const v = e.target.value;
                  setTipoSel(v);
                  if (v === "") setTipoDetalle("");
                  else if (v !== LIBRE) {
                    const t = tiposActivos.find((x) => x.id === parseInt(v));
                    if (t) setTipoDetalle(t.nombre);
                  }
                  // __libre__: el usuario escribe abajo
                }}
              >
                <option value="">
                  {esFallaEdit
                    ? "— elige el tipo de falla —"
                    : "— elige el tipo de solicitud (opcional) —"}
                </option>
                {tiposActivos
                  .filter((t) => t.activo)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nombre}
                    </option>
                  ))}
                <option value={LIBRE}>Escribir manualmente...</option>
              </select>
              {tipoSel === LIBRE && (
                <input
                  className="input mt-1 w-full"
                  value={tipoDetalle}
                  onChange={(e) => setTipoDetalle(e.target.value)}
                  placeholder={`Describe el ${tipoLabel.toLowerCase()}`}
                  autoFocus
                />
              )}
              {!esFallaEdit && tiposSolicitud.length === 0 && (
                <p className="text-[10px] text-zinc-500 mt-1">
                  Da de alta los tipos comunes en Configuración → Tipos de
                  solicitud.
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={guardar}
              disabled={guardando}
              className="btn-primary text-xs py-1 flex-1 sm:flex-none"
            >
              {guardando ? "Guardando..." : "Guardar"}
            </button>
            <button
              onClick={() => setEditando(false)}
              disabled={guardando}
              className="btn-ghost text-xs py-1 flex-1 sm:flex-none"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
